import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";

const PLANS = [
  {
    name: "Discovery Call",
    price: "Free",
    detail: "30 minutes",
    desc: "Discuss your idea, constraints, and whether we’re a fit. No obligation.",
    features: ["Goal & scope discussion", "Rough timeline guidance", "Next-step recommendation"],
    cta: "Book a call",
    href: "/book-call",
    highlight: false,
  },
  {
    name: "Scoping Sprint",
    price: "₹49,000",
    detail: "starting from",
    desc: "One-week product scoping with requirements, architecture outline, and a written SOW.",
    features: ["Requirements workshop", "Architecture sketch", "Fixed-scope SOW & estimate"],
    cta: "Start scoping",
    href: "/#cta",
    highlight: false,
  },
  {
    name: "Web / SaaS MVP",
    price: "₹2,50,000",
    detail: "starting from",
    desc: "Production-ready web or SaaS MVP — design, build, and launch support.",
    features: ["UI + core product flows", "Auth, APIs, deployment", "2 weeks post-launch support"],
    cta: "Request quote",
    href: "/#cta",
    highlight: true,
  },
  {
    name: "AI / Hardware Build",
    price: "₹3,00,000",
    detail: "starting from",
    desc: "AI-native products or hardware/IoT prototypes with integrated software.",
    features: ["AI or embedded scope", "Prototype → pilot path", "Custom milestones"],
    cta: "Request quote",
    href: "/#cta",
    highlight: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="relative py-24 md:py-32 border-y border-white/5"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14 md:mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            / Pricing
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="pricing-heading"
          >
            Clear starting prices.
            <span className="block text-zinc-500">Custom SOW for larger work.</span>
          </motion.h2>
          <p className="mt-5 text-zinc-400 max-w-2xl leading-relaxed">
            Discovery is free. Packages below are starting points — final pricing is confirmed in a
            Statement of Work (SOW) before any payment.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`pricing-plan-${i}`}
              className={`relative flex flex-col rounded-2xl border p-6 md:p-7 ${
                plan.highlight
                  ? "border-accent-cyan/40 bg-white/[0.04]"
                  : "border-white/10 bg-ink-50/40"
              }`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                {plan.detail}
              </div>
              <h3 className="font-display text-xl tracking-tight mb-1">{plan.name}</h3>
              <div className="font-display text-3xl tracking-tight text-white mb-3">
                {plan.price}
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 flex-grow">{plan.desc}</p>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check size={14} className="mt-0.5 shrink-0 text-accent-cyan" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={plan.href}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white hover:bg-white/5 hover:border-white/30 transition-colors"
              >
                {plan.cta}
                <ArrowRight size={14} strokeWidth={2.5} />
              </a>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-sm text-zinc-500 max-w-3xl leading-relaxed">
          Larger or multi-discipline engagements are priced via a custom SOW after scoping. Taxes
          (GST) extra where applicable. See our{" "}
          <a href="/refund" className="text-zinc-300 underline hover:text-white">
            Refund &amp; Cancellation Policy
          </a>{" "}
          before paying.
        </p>
      </div>
    </section>
  );
}
